<template>
  <div>
    <template v-if="status === SignatureStatus.Signed">
      <div class="d-flex align-center">
        <font-awesome-icon :icon="['fad', 'circle-check']"
                           style="font-size: 33px; --fa-primary-color: #7bb287; --fa-secondary-color: #7bb287"
                           class="mr-2"
                           data-locator="you-signed-the-document-info"
        />
        {{ $t('project.signed-documents.SignedDocumentsTableItemOptions.youSignedTheDocument') }}
      </div>
    </template>

    <template v-if="status === SignatureStatus.SignedByAll">
      <div class="d-flex align-center" style="justify-content: end">
        <font-awesome-icon :icon="['fad', 'circle-check']"
                           style="font-size: 24px; --fa-primary-color: #7bb287; --fa-secondary-color: #7bb287"
                           class="mr-2"
                           data-locator="check-icon"
        />
        <template v-if="hasOriginal">
          <app-button size="small"
                      type="outlined"
                      variant="neutral"
                      width="110"
                      data-locator="download-button"
                      @click.stop="openSingleFileDownloadDialog = true"
          >
            <font-awesome-icon :icon="['fal','file-download']"
                               style="font-size: 16px;"
                               class="mr-2"
            />
            {{ $t('project.signed-documents.SignedDocumentsTableItemOptions.documentDownload') }}
          </app-button>
          <SignedDocumentsDownloadDialog v-if="openSingleFileDownloadDialog"
                                         :signed-document="item"
                                         @close="openSingleFileDownloadDialog = false"
          />
          <AppTooltip>
            <template #activator="{ props }">
              <div v-bind="props">
                <v-menu>
                  <template #activator="{ props: menuProps }">
                    <app-button class="ml-2" data-locator="other-actions-menu-button" type="icon" v-bind="menuProps">
                      <font-awesome-icon :icon="['far','ellipsis-vertical']"
                                         style="font-size: 16px;"
                      />
                    </app-button>
                  </template>
                  <v-list data-locator="signed-document-menu">
                    <v-list-item data-locator="share-menu-item" link @click="openShareOriginalsPane([item])">
                      <v-list-item-title>
                        {{ $t('project.signed-documents.SignedDocumentsTableItemOptions.menu.share') }}
                      </v-list-item-title>
                    </v-list-item>
                    <ArchiveAddAccessListItem data-locator="archive-menu-item" :routeOptions="archiveAddRouteOptions" :icon="false" />
                    <v-list-item data-locator="delete-menu-item" link @click="openDeleteOriginalsDialog([item])">
                      <v-list-item-title>
                        {{ $t('project.signed-documents.SignedDocumentsTableItemOptions.menu.delete') }}
                      </v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="imanageEnabledAndLogged" data-locator="imanage-export-menu-item" link @click="openExportToIManageDialog(item.id)">
                      <v-list-item-title>
                        {{ $t('project.signed-documents.SignedDocumentsTableItemOptions.menu.exportToImanage') }}
                      </v-list-item-title>
                    </v-list-item>
                      <PolyOfficeListItemExport v-if="polyOfficeEnabled"
                                                :fileSize="combinedSignedDocumentSize"
                                                @polyOfficeOpenDialog="polyOfficeOpenDialogHandler"
                      />
                  </v-list>
                </v-menu>
              </div>
            </template>
            <span>{{ $t('common.additionalActions') }}</span>
          </AppTooltip>
        </template>
        <template v-else>
          <app-button disabled @click.stop>
            {{ $t('project.signed-documents.SignedDocumentsTableItemOptions.noAccessToOriginal') }}
          </app-button>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import ArchiveAddAccessListItem from '@/common/archives/ArchiveAddAccessListItem.vue'
import { SignedEnvExport } from '@/common/polyOffice/polyOffice.model'
import PolyOfficeListItemExport from '@/common/polyOffice/PolyOfficeListItemExport.vue'
import { SignatureStatus } from '@/common/utils/signatureStatus'
import { toast } from '@/common/utils/toast'
import { statusDataMap } from '@/project/signed-documents/signedDocumentsDataMap'
import { GET_EXPORT_IFRAME } from '@/store/modules/poly-office/action_types'

import SignedDocumentsDownloadDialog from './dialogs/SignedDocumentsDownloadDialog.vue'

export default {
  name: 'SignedDocumentsTableItemOptions',
  components: {
    AppTooltip,
    ArchiveAddAccessListItem,
    PolyOfficeListItemExport,
    SignedDocumentsDownloadDialog,
  },
  inject: [
    'openDeleteOriginalsDialog',
    'openExportToIManageDialog',
    'openShareOriginalsPane',
  ],
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      SignatureStatus,
      status: statusDataMap(this.item),
      openSingleFileDownloadDialog: false,
    }
  },
  computed: {
    ...mapGetters('room', ['roomMnemo']),
    ...mapGetters('imanage', ['imanageEnabledAndLogged']),
    ...mapGetters('user', ['polyOfficeEnabled']),
    hasOriginal () {
      return this.status === SignatureStatus.SignedByAll && (this.item.proofFileURL || this.item.signedDocumentURL)
    },
    combinedSignedDocumentSize () {
      return this.item.signSize + this.item.proofSize
    },
    archiveAddRouteOptions () {
      return {
        state: {
          signedDocuments: [this.item],
        },
        query: {
          signedDocumentIds: this.item.id,
          fromMnemo: this.roomMnemo,
        },
      }
    },
  },
  methods: {
    ...mapActions('polyOffice', [
      GET_EXPORT_IFRAME,
    ]),
    async polyOfficeOpenDialogHandler () {
      const payload = new SignedEnvExport(
        this.roomMnemo,
        [this.item.id],
      )
      try {
        await this.GET_EXPORT_IFRAME({
          payload,
          successMessage: this.$t('project.signed-documents.SignedDocumentsTableItemOptions.polyOffice.exportSuccessful'),
        })
      } catch (error) {
        console.error(error)
        toast.error(this.$t('common.polyOfficeErrors.ERR_FILE_TOO_LARGE', 1))
      }
    },
  },
}
</script>
